// src/modules/requerimiento/components/RequerimientoPendientesList.jsx
// Tarjetas de "Mis pendientes", con el diseño de la app: franja y ícono del color del tipo
// (adición verde, mantenimiento ámbar, devolución rojo), etapa actual, aviso de atraso según la
// fecha de coordinación, datos con íconos y el creador al pie.
import React from "react";
import {
  Avatar, Box, Card, CardActionArea, CircularProgress, Pagination, Stack, Typography,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import BuildIcon from "@mui/icons-material/Build";
import AssignmentReturnIcon from "@mui/icons-material/AssignmentReturn";
import AssignmentIcon from "@mui/icons-material/Assignment";
import EventIcon from "@mui/icons-material/Event";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import EngineeringIcon from "@mui/icons-material/Engineering";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import InboxIcon from "@mui/icons-material/Inbox";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ENV from "../../../config/env";

const TIPOS = [
  { patron: /adici/i,     color: "#16A34A", Icono: AddCircleOutlineIcon },
  { patron: /mantenim/i,  color: "#F59E0B", Icono: BuildIcon },
  { patron: /devoluci/i,  color: "#DC2626", Icono: AssignmentReturnIcon },
];
const TIPO_OTRO = { color: "#6366F1", Icono: AssignmentIcon };
const tipoDe = (nombre) => TIPOS.find((t) => t.patron.test(String(nombre || ""))) || TIPO_OTRO;

const ETAPAS = {
  1: "Coordinación",
  2: "Programación",
  3: "Trabajo de campo",
  4: "Trabajo de campo",
  7: "Revisión en Sistemas",
  8: "Finalizado",
};

const fmtFecha = (v) => {
  const d = new Date(v);
  if (!v || Number.isNaN(d.getTime())) return "";
  // En UTC, igual que diasDesde: la fecha llega como medianoche y en hora local cambiaría de día.
  return d.toLocaleDateString("es-BO", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "UTC" });
};
const fmtHora = (h) => (h ? String(h).slice(0, 5) : "");

/** Días desde la fecha de coordinación (hoy = 0; futuro negativo); null si no hay fecha. */
const diasDesde = (v) => {
  const d = new Date(v);
  if (!v || Number.isNaN(d.getTime())) return null;
  const hoy = new Date();
  const a = Date.UTC(hoy.getFullYear(), hoy.getMonth(), hoy.getDate());
  const b = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  return Math.round((a - b) / 86400000);
};

/** Aviso de fecha: solo mientras el trabajo no se hizo (hasta Trabajo de campo). */
function avisoFecha(r) {
  if (![1, 2, 3, 4].includes(Number(r?.estado))) return null;
  const dias = diasDesde(r?.fecha_coordinacion);
  if (dias == null) return null;
  if (dias > 0) return { texto: dias === 1 ? "Atrasado 1 día" : `Atrasado ${dias} días`, color: "error.main" };
  if (dias === 0) return { texto: "Para hoy", color: "#B45309" };
  if (dias === -1) return { texto: "Mañana", color: "text.secondary" };
  return null;
}

export default function RequerimientoPendientesList({
  items,
  loading,
  errorMsg,
  page,
  pageCount,
  totalItems,
  onPageChange,
  onOpen,
}) {
  if (loading) {
    return (
      <Stack alignItems="center" sx={{ py: 8 }} spacing={1.5}>
        <CircularProgress size={28} />
        <Typography variant="body2" color="text.secondary">Cargando pendientes…</Typography>
      </Stack>
    );
  }

  if (errorMsg) {
    return (
      <Stack alignItems="center" sx={{ py: 8, textAlign: "center" }} spacing={0.75}>
        <ErrorOutlineIcon color="error" sx={{ fontSize: 40 }} />
        <Typography sx={{ fontWeight: 700 }}>{errorMsg}</Typography>
        <Typography variant="body2" color="text.secondary">Verifica tu conexión e inténtalo nuevamente.</Typography>
      </Stack>
    );
  }

  if (!items || items.length === 0) {
    return (
      <Stack alignItems="center" sx={{ py: 8, textAlign: "center" }} spacing={0.75}>
        <InboxIcon sx={{ fontSize: 44, color: "text.disabled" }} />
        <Typography sx={{ fontWeight: 700 }}>No hay requerimientos pendientes</Typography>
        <Typography variant="body2" color="text.secondary">Prueba con otro técnico o cambia la búsqueda.</Typography>
      </Stack>
    );
  }

  return (
    <>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(3, 1fr)" }, gap: 2 }}>
        {items.map((r) => <PendienteCard key={r.cod} r={r} onOpen={onOpen} />)}
      </Box>

      {pageCount > 1 && (
        <Stack alignItems="center" sx={{ mt: 3 }}>
          <Pagination
            count={pageCount}
            page={page}
            onChange={(_, value) => onPageChange?.(value)}
            shape="rounded"
            color="primary"
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
            Mostrando {items.length} de {totalItems}
          </Typography>
        </Stack>
      )}
    </>
  );
}

function PendienteCard({ r, onOpen }) {
  const tipo = tipoDe(r?.nombre_ot_tipo);
  const etapa = ETAPAS[Number(r?.estado)] || "";
  const aviso = avisoFecha(r);
  const placas = String(r?.placa || "").split(/[,;]+/).map((p) => p.trim()).filter(Boolean);
  const fecha = [fmtFecha(r?.fecha_coordinacion), fmtHora(r?.hora_coordinacion)].filter(Boolean).join(" · ");
  const avatar = r?.profile_picture ? ENV.API_BASE_URL + r.profile_picture : undefined;

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: "10px", height: "100%", display: "flex", overflow: "hidden",
        transition: "box-shadow .15s, border-color .15s",
        "&:hover": { borderColor: tipo.color, boxShadow: 2 },
      }}
    >
      <Box sx={{ width: 5, flexShrink: 0, bgcolor: tipo.color }} />
      <CardActionArea
        onClick={() => onOpen(r.cod)}
        sx={{ display: "flex", flexDirection: "column", alignItems: "stretch", justifyContent: "flex-start", p: 1.75 }}
      >
        {/* Cabecera: tipo, código, etapa y aviso de fecha */}
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <Box sx={{ width: 40, height: 40, borderRadius: "8px", display: "grid", placeItems: "center", flexShrink: 0, bgcolor: `${tipo.color}1F`, color: tipo.color }}>
            <tipo.Icono fontSize="small" />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 800, lineHeight: 1.2 }}>#{r.cod ?? "—"}</Typography>
            <Typography variant="caption" sx={{ color: tipo.color, fontWeight: 700 }} noWrap component="div">
              {r?.nombre_ot_tipo || "Requerimiento"}{etapa ? <Box component="span" sx={{ color: "text.secondary", fontWeight: 500 }}> · {etapa}</Box> : null}
            </Typography>
          </Box>
          {aviso && (
            <Typography variant="caption" sx={{ color: aviso.color, fontWeight: 700, flexShrink: 0 }}>{aviso.texto}</Typography>
          )}
        </Stack>

        {/* Cliente */}
        <Box sx={{ mt: 1.25 }}>
          <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap title={r?.razon_social || ""}>
            {r?.razon_social || "Sin razón social"}
          </Typography>
          {(r?.descripcion || r?.sucursal) && (
            <Typography variant="caption" color="text.secondary" noWrap component="div">
              {[r?.descripcion, r?.sucursal].filter(Boolean).join(" · ")}
            </Typography>
          )}
        </Box>

        {/* Datos */}
        <Stack spacing={0.5} sx={{ mt: 1.25 }}>
          {fecha && <Linea Icono={EventIcon} texto={fecha} />}
          {placas.length > 0 && (
            <Linea
              Icono={DirectionsCarIcon}
              texto={placas.slice(0, 3).join(", ") + (placas.length > 3 ? ` +${placas.length - 3}` : "")}
              title={placas.join(", ")}
            />
          )}
          {r?.tecnico && <Linea Icono={EngineeringIcon} texto={r.tecnico} />}
          {r?.solicitante && <Linea Icono={PersonOutlineIcon} texto={r.solicitante} />}
        </Stack>

        {r?.observacion && (
          <Typography
            variant="body2"
            color="text.secondary"
            title={r.observacion}
            sx={{
              mt: 1, p: 1, borderRadius: "8px", bgcolor: "action.hover",
              overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
            }}
          >
            {r.observacion}
          </Typography>
        )}

        {/* Pie: creador */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: "auto", pt: 1.5 }}>
          <Avatar src={avatar} sx={{ width: 24, height: 24, fontSize: 12 }}>
            {(r?.usu ?? "?").charAt(0).toUpperCase()}
          </Avatar>
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1, minWidth: 0 }} noWrap>
            Creado por {r?.usu || "—"}
          </Typography>
          <Typography variant="caption" sx={{ color: tipo.color, fontWeight: 700, display: "flex", alignItems: "center" }}>
            Ver detalle <ChevronRightIcon sx={{ fontSize: 18 }} />
          </Typography>
        </Stack>
      </CardActionArea>
    </Card>
  );
}

function Linea({ Icono, texto, title }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1} title={title}>
      <Icono sx={{ fontSize: 16, color: "text.secondary" }} />
      <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>{texto}</Typography>
    </Stack>
  );
}
